import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  AlertTitle,
  Button,
  HStack,
  List,
  ListItem,
  Text,
  VStack,
} from '@chakra-ui/react';
import { bytesToMb } from '@lib/util/units';
import type { OversizedFile } from '@snap-types/actions/send-snap-attachments';
import { useCurrentSiteCode } from '@ui/hooks/useCurrentSite';
import { useRef } from 'react';

interface OversizedFilesDialogProps {
  isOpen: boolean;
  fileSizeLimitInBytes: number;
  oversizedFiles: OversizedFile[];
  isLoading: boolean;
  onClose: () => void;
  onSendWithoutImports: () => void;
  onSendAnyway: () => void;
}

export function OversizedFilesDialog({
  isOpen,
  fileSizeLimitInBytes,
  oversizedFiles,
  isLoading,
  onClose,
  onSendWithoutImports,
  onSendAnyway,
}: OversizedFilesDialogProps) {
  const siteCode = useCurrentSiteCode();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const formatFileSize = (bytes: number): string => {
    return `${bytesToMb(bytes).toFixed(2)} MB`;
  };

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      size="xl"
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Import Files Exceed Size Limit
          </AlertDialogHeader>

          <AlertDialogBody>
            <VStack align="stretch" spacing={4}>
              <Alert status="warning">
                <AlertIcon />
                <VStack align="start" spacing={1}>
                  <AlertTitle>Large Import Files Detected</AlertTitle>
                  <AlertDescription>
                    The following import files exceed {siteCode}’s size limit of{' '}
                    {bytesToMb(fileSizeLimitInBytes)} MB:
                  </AlertDescription>
                </VStack>
              </Alert>

              <List spacing={2}>
                {oversizedFiles.map((file, index) => (
                  <ListItem
                    key={index}
                    p={2}
                    borderWidth="1px"
                    borderRadius="md"
                    bg="gray.50"
                  >
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="semibold">{file.fileName}</Text>
                      <HStack spacing={4} fontSize="sm" color="gray.600">
                        <Text>
                          Size:{' '}
                          <strong>{formatFileSize(file.fileSizeBytes)}</strong>
                        </Text>
                        <Text>
                          Exceeds by:{' '}
                          <strong style={{ color: '#C53030' }}>
                            {formatFileSize(file.excessBytes)}
                          </strong>
                        </Text>
                      </HStack>
                    </VStack>
                  </ListItem>
                ))}
              </List>

              <Text fontSize="sm" color="gray.600">
                You can choose to send the quote anyway (ignoring the size
                warning), send without the large import files (other import
                files will still be included), or cancel and decide later.
              </Text>
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter gap={3}>
            <Button ref={cancelRef} onClick={onClose} rounded="sm">
              Cancel
            </Button>
            <Button
              variant="outline"
              colorScheme="blue"
              onClick={onSendWithoutImports}
              isLoading={isLoading}
              rounded="sm"
            >
              Send Without These Files
            </Button>
            <Button
              colorScheme="orange"
              onClick={onSendAnyway}
              isLoading={isLoading}
              rounded="sm"
            >
              Send Anyway
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
